const STORAGE_PREFIX = 'nl_popup_dismissed_';

/**
 * Drives the per-post/page newsletter popups: trigger (delay/scroll),
 * open/close, permanent per-preset suppression, and ebook file download.
 * Subscription itself is handled by the shared Newsletter module, which
 * emits a `newsletter:success` event we listen for here.
 */
export default class NewsletterPopup {
    constructor() {
        this.popups = [...document.querySelectorAll('.jsNewsletterPopup')];
        this.popups.forEach((popup) => this.initPopup(popup));
    }

    isSuppressed(key) {
        try {
            return !!window.localStorage.getItem(STORAGE_PREFIX + key);
        } catch (e) {
            return false;
        }
    }

    suppress(key) {
        if (!key) return;
        try {
            window.localStorage.setItem(STORAGE_PREFIX + key, '1');
        } catch (e) {
            /* storage unavailable — popup simply re-shows next visit */
        }
    }

    initPopup(popup) {
        const key = popup.dataset.presetKey || '';

        if (key && this.isSuppressed(key)) {
            return;
        }

        this.bindClose(popup, key);
        this.bindSuccess(popup, key);
        this.scheduleOpen(popup);
    }

    open(popup) {
        if (popup.open) return;
        if (typeof popup.showModal === 'function') {
            popup.showModal();
        }
    }

    scheduleOpen(popup) {
        if (popup.dataset.triggerType === 'scroll') {
            const percent = parseInt(popup.dataset.scroll, 10) || 0;

            const onScroll = () => {
                const total = document.documentElement.scrollHeight;
                const viewport = window.innerHeight;
                const reached =
                    total <= viewport
                        ? true
                        : (window.scrollY / (total - viewport)) * 100 >=
                          percent;

                if (reached) {
                    window.removeEventListener('scroll', onScroll);
                    this.open(popup);
                }
            };

            window.addEventListener('scroll', onScroll, { passive: true });
            onScroll();
        } else {
            const delay = (parseInt(popup.dataset.delay, 10) || 0) * 1000;
            window.setTimeout(() => this.open(popup), delay);
        }
    }

    bindClose(popup, key) {
        const close = () => {
            this.suppress(key);
            popup.close();
        };

        [...popup.querySelectorAll('.jsNewsletterPopupClose')].forEach((btn) =>
            btn.addEventListener('click', close),
        );

        // Backdrop click (click registered on the dialog itself, not content).
        popup.addEventListener('click', (e) => {
            if (e.target === popup) close();
        });

        // ESC key fires the native `cancel` event.
        popup.addEventListener('cancel', () => this.suppress(key));
    }

    bindSuccess(popup, key) {
        popup.addEventListener('newsletter:success', (e) => {
            this.suppress(key);

            const payload = e.detail;
            const fileUrl =
                payload && typeof payload === 'object'
                    ? payload.file_url
                    : null;

            if (fileUrl) {
                this.triggerDownload(fileUrl);
            }
        });
    }

    triggerDownload(url) {
        const link = document.createElement('a');
        link.href = url;
        link.download = '';
        link.rel = 'noopener';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    }
}
